import React, { useContext } from 'react'
import styled, { css, ThemeContext } from 'styled-components'

// Styles
import { shadows } from '../../styles/effects'
import { applyTextStyle, applyTextEllipsis } from '../../styles/typography'

// Components
import { Icon, IconWrapper } from '../icon'
import Link from '../Link'
import Text from '../Text'

import { IFormControlProps } from '../../common/models/IFormControlProps'

export enum InputType {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Text = 'text',
  Password = 'password',
  Date = 'date',
  File = 'file',
  Email = 'email',
}

export interface IProps extends IFormControlProps {
  type?: InputType
  placeholder?: string
  withSearch?: boolean
  coloredBackground?: boolean
  className?: string
  maxLength?: number
  bottomCaption?: string
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  button?: {
    label: string
    handleClick: () => void
    disabled?: boolean
  }
  /** Handle for the e2e suite. Spread onto the <input> with the rest of the
      props, so anything built on Input (SuggestInput) inherits it. */
  'data-cy'?: string
}

const Input = ({
  className,
  label,
  additionalLabel,
  coloredBackground,
  bottomCaption,
  ...props
}: IProps) => {
  const themeContext = useContext(ThemeContext)
  const type = props.type || InputType.Text
  const errorMessage =
    props.meta && props.meta.error && props.meta.touched
      ? props.meta.error
      : props.errorMessage
  // Counter reflects the displayed value, whether driven directly or via formik
  const currentValue = props.value ?? props.meta?.value ?? ''
  const valueLength = typeof currentValue === 'string' ? currentValue.length : 0

  return (
    <InputWrapper
      className={className}
      disabled={props.disabled}
      errorMessage={errorMessage}
    >
      {label && (
        <InputLabel
          textStyle="labelTinyCaps"
          marginBottom={additionalLabel ? 4 : 8}
        >
          {label}
        </InputLabel>
      )}
      {label && additionalLabel && (
        <InputLabel textStyle="caption" color="textLight" marginBottom={8}>
          {additionalLabel}
        </InputLabel>
      )}
      <InputInnerWrapper>
        <StyledInput
          type={type}
          {...props}
          coloredBackground={coloredBackground}
          withButton={
            props.button !== undefined || props.maxLength !== undefined
          }
        />
        {props.withSearch && (
          <Icon type="Search" size={16} color={themeContext.colors.textLight} />
        )}
        {props.maxLength && (
          <StyledText
            color={
              valueLength === props.maxLength ? 'secondary' : 'textLighter'
            }
            textStyle="label"
          >
            {valueLength} / {props.maxLength}
          </StyledText>
        )}
        {props.button && (
          <StyledLink
            caption={props.button.label}
            onClick={props.button.handleClick}
            disabled={props.button.disabled}
            disableUnderline
            color="primary"
          />
        )}
      </InputInnerWrapper>
      {bottomCaption && (
        <SmallCaption textStyle="captionSmall" color="textLight" marginTop={4}>
          {bottomCaption}
        </SmallCaption>
      )}
      {errorMessage && (
        <SmallCaption textStyle="captionSmall" color="secondary" marginTop={4}>
          {errorMessage}
        </SmallCaption>
      )}
    </InputWrapper>
  )
}

const InputLabel = styled(Text)`
  padding-left: 1px;
  ${applyTextEllipsis()}
`

const InputInnerWrapper = styled.div`
  position: relative;

  ${IconWrapper} {
    position: absolute;
    top: 50%;
    left: 16px;
    transform: translateY(-50%);
  }
`

const StyledLink = styled(Link)`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
`

const StyledText = styled(Text)`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
`

const StyledInput = styled.input<{
  withSearch?: boolean
  withButton?: boolean
  coloredBackground?: boolean
}>`
  display: block;
  width: 100%;
  padding: ${({ withSearch, withButton }) =>
    withSearch
      ? '12px 12px 12px 48px'
      : withButton
      ? '12px 56px 12px 12px'
      : '12px'};
  border: 1px solid ${({ theme }) => theme.colors.textLighter};
  border-radius: 8px;
  outline: 0;
  box-shadow: ${shadows.depthOne};
  ${applyTextStyle('bodySmall')}
  color: ${({ theme }) => theme.colors.text};
  background-color: ${({ theme, coloredBackground }) =>
    coloredBackground ? theme.colors.primaryLight : theme.colors.lightest};
  transition: border-color 0.3s ease;

  &:disabled {
    pointer-events: none;
    color: ${({ theme }) => theme.colors.textLighter};
    background-color: ${({ theme }) => theme.colors.primaryLight};
  }

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};

    & + * path {
      fill: ${({ theme }) => theme.colors.primary};
    }
  }

  ::placeholder {
    color: ${({ theme }) => theme.colors.text72};
  }
`

const InputWrapper = styled.div<
  Pick<IFormControlProps, 'disabled' | 'errorMessage'>
>`
  position: relative;
  display: block;

  ${({ errorMessage, theme }) =>
    errorMessage &&
    css`
      ${StyledInput} {
        border-color: ${theme.colors.secondary};

        & + * path {
          fill: ${theme.colors.secondary};
        }
      }
    `}

  ${({ disabled, theme }) =>
    disabled &&
    css`
      ${InputLabel}, ${SmallCaption} {
        color: ${theme.colors.text72};
      }
    `}
`

const SmallCaption = styled(Text)`
  padding-left: 1px;
  ${applyTextEllipsis()}
`

export default Input
